import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableRow
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { getStockById, type StockItem as StockType } from '../../../shared/services/stockService';

export const StockDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [stock, setStock] = useState<StockType | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!id) {
            setError('Invalid stock ID');
            setLoading(false);
            return;
        }
        const fetchStock = async () => {
            try {
                setLoading(true);
                setError('');
                const response = await getStockById(id);
                if (response.success && response.data) {
                    setStock(response.data);
                } else {
                    setError('Stock not found');
                }
            } catch {
                setError('Failed to load stock');
                setStock(null);
            } finally {
                setLoading(false);
            }
        };
        fetchStock();
    }, [id]);

    if (loading) {
        return (
            <div className="flex flex-col flex-auto min-w-0">
                <div className="flex flex-auto p-6 sm:p-10 items-center justify-center min-h-[300px]">
                    <CircularProgress />
                </div>
            </div>
        );
    }

    if (error || !stock) {
        return (
            <div className="flex flex-col flex-auto min-w-0">
                <div className="p-6 sm:p-10">
                    <AppButton variant="secondary" startIcon={<ArrowBackIcon />} onClick={() => navigate('/stock')}>
                        Back to Stocks
                    </AppButton>
                    <Box mt={2}>
                        <Alert severity="error">{error || 'Stock not found'}</Alert>
                    </Box>
                </div>
            </div>
        );
    }

    const infoRows: { label: string; value: string | number | undefined | null }[] = [
        { label: 'Item Name', value: stock.itemName },
        { label: 'Reference Code', value: stock.itemReferenceCode },
        { label: 'ID', value: stock.id },
        { label: 'Outlet ID', value: stock.outletId },
        { label: 'Stock', value: stock.stock },
        { label: 'Buffer Stock', value: stock.bufferStock },
        { label: 'Timestamp', value: stock.timestamp },
        { label: 'Created At', value: stock.createdAt ? new Date(stock.createdAt).toLocaleString() : null },
        { label: 'Updated At', value: stock.updatedAt ? new Date(stock.updatedAt).toLocaleString() : null }
    ];

    return (
        <div className="flex flex-col flex-auto min-w-0">
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex items-center gap-4">
                    <AppButton variant="secondary" startIcon={<ArrowBackIcon />} onClick={() => navigate('/stock')}>
                        Back to Stocks
                    </AppButton>
                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{stock.itemName}</h2>
                </div>
            </div>

            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col gap-6 w-full max-w-2xl">
                    <Paper className="p-4 sm:p-6 !shadow-sm !border !border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Stock details</h3>
                        <Table size="small">
                            <TableBody>
                                {infoRows.map((row) => (
                                    <TableRow key={row.label}>
                                        <TableCell className="!font-medium !text-gray-600 !w-40">
                                            {row.label}
                                        </TableCell>
                                        <TableCell>{row.value ?? '–'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </div>
            </div>
        </div>
    );
};
